/**
 * ManageDataTab.jsx
 * -----------------
 * Tab for managing data records with CRUD operations and CSV export
 */

import React, { useState, useEffect, useRef } from 'react';
import { 
  Download, 
  Upload, 
  Trash2, 
  Plus, 
  RefreshCw,
  Database,
  AlertCircle
} from 'lucide-react';
import DataTable from './DataTable';
import RecordModal from './RecordModal';
import dataManagementService from '../../services/dataManagementService';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ManageDataTab = ({ appPrefix, userId }) => {
  const [schemas, setSchemas] = useState([]);
  const [selectedSchema, setSelectedSchema] = useState(null);
  const [records, setRecords] = useState([]);
  const [recordCount, setRecordCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingSchemas, setLoadingSchemas] = useState(true);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [rawApiData, setRawApiData] = useState(null); // Debug: store raw API response
  
  // Modal states
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [editingRecord, setEditingRecord] = useState(null);
  
  // Ref to track if schemas have been loaded to prevent duplicate toasts
  const schemasLoadedRef = useRef(false);

  // Load schemas on mount (without toast to prevent duplicates)
  useEffect(() => {
    schemasLoadedRef.current = false;

    const fetchSchemas = async () => {
      setLoadingSchemas(true);
      try {
        console.log('[ManageDataTab] ============ LOADING SCHEMAS ============');
        console.log('[ManageDataTab] appPrefix:', appPrefix);
        console.log('[ManageDataTab] userId:', userId);

        const data = await dataManagementService.getTableSchemas(appPrefix);

        console.log('[ManageDataTab] ============ RESULT ============');
        console.log('[ManageDataTab] Type of data:', typeof data);
        console.log('[ManageDataTab] Is array:', Array.isArray(data));
        console.log('[ManageDataTab] Length:', data?.length);
        console.log('[ManageDataTab] Data:', JSON.stringify(data, null, 2));

        // Store raw data for debugging
        setRawApiData(data);
        setSchemas(data);

        // Auto-select first schema if available
        if (data.length > 0) {
          console.log('[ManageDataTab] Auto-selecting first schema:', data[0]);
          setSelectedSchema(data[0]);
        }

        if (!schemasLoadedRef.current) {
          schemasLoadedRef.current = true;
        }
      } catch (error) {
        console.error('[ManageDataTab] ============ ERROR ============');
        console.error('[ManageDataTab] Error:', error);
        console.error('[ManageDataTab] Error message:', error.message);
        setRawApiData({ error: error.message });
      } finally {
        setLoadingSchemas(false);
      }
    };

    fetchSchemas();
  }, [appPrefix, userId]);

  // Load records when schema changes
  useEffect(() => {
    if (!selectedSchema) return;

    const fetchRecords = async () => {
      setLoading(true);
      try {
        const result = await dataManagementService.getRecords(
          appPrefix,
          selectedSchema.tableName,
          { pageSize: 1000 }
        );
        setRecords(result.records);

        // Update record count from result or schema
        setRecordCount(result.total || selectedSchema.recordCount || result.records.length);
      } catch (error) {
        console.error('Failed to load records:', error);
        toast.error('Failed to load records');
        setRecords([]);
        setRecordCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchRecords();
  }, [selectedSchema, appPrefix]);

  // Standalone version of loadSchemas kept for on-demand calls (refresh button, post-delete)
  const loadSchemas = async (showToast = true) => {
    setLoadingSchemas(true);
    try {
      const data = await dataManagementService.getTableSchemas(appPrefix);

      setRawApiData(data);
      setSchemas(data);

      if (data.length > 0 && !selectedSchema) {
        setSelectedSchema(data[0]);
      } else if (selectedSchema) {
        const updatedSchema = data.find(s => s.id === selectedSchema.id);
        if (updatedSchema) {
          setSelectedSchema(updatedSchema);
        }
      }

      if (showToast) {
        if (data.length > 0) {
          toast.success(`Loaded ${data.length} table(s)`);
        } else {
          toast.warning(`No schemas returned. Check console for details.`);
        }
      }
    } catch (error) {
      console.error('[ManageDataTab] Error:', error);
      if (showToast) {
        toast.error('Failed to load tables: ' + (error.message || 'Unknown error'));
      }
      setRawApiData({ error: error.message });
    } finally {
      setLoadingSchemas(false);
    }
  };

  // Standalone version of loadRecords kept for on-demand calls (refresh button, post-save/delete)
  const loadRecords = async () => {
    if (!selectedSchema) return;

    setLoading(true);
    try {
      const result = await dataManagementService.getRecords(
        appPrefix,
        selectedSchema.tableName,
        { pageSize: 1000 }
      );
      setRecords(result.records);
      setRecordCount(result.total || selectedSchema.recordCount || result.records.length);
    } catch (error) {
      console.error('Failed to load records:', error);
      toast.error('Failed to load records');
      setRecords([]);
      setRecordCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSchemaChange = (schemaId) => {
    const schema = schemas.find(s => s.id === schemaId);
    setSelectedSchema(schema);
    setSelectedRecords([]);
  };

  const handleAddRecord = () => {
    setModalMode('create');
    setEditingRecord(null);
    setShowRecordModal(true);
  };

  const handleEditRecord = (record) => {
    setModalMode('edit');
    setEditingRecord(record);
    setShowRecordModal(true);
  };

  const handleDeleteRecord = async (record) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete this record? This action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      await dataManagementService.deleteRecord(
        appPrefix,
        selectedSchema.tableName,
        record.id
      );
      toast.success('Record deleted successfully');
      loadRecords();
    } catch (error) {
      console.error('Failed to delete record:', error);
      toast.error('Failed to delete record');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRecords.length === 0) {
      toast.warning('Please select records to delete');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedRecords.length} record(s)? This action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      await dataManagementService.bulkDeleteRecords(
        appPrefix,
        selectedSchema.tableName,
        selectedRecords
      );
      toast.success(`Deleted ${selectedRecords.length} record(s)`);
      setSelectedRecords([]);
      loadRecords();
    } catch (error) {
      console.error('Failed to delete records:', error);
      toast.error('Failed to delete records');
    }
  };

  const handleSaveRecord = async (data) => {
    try {
      if (modalMode === 'create') {
        await dataManagementService.createRecord(
          appPrefix,
          selectedSchema.tableName,
          data,
          userId
        );
        toast.success('Record created successfully');
      } else {
        await dataManagementService.updateRecord(
          appPrefix,
          selectedSchema.tableName,
          editingRecord.id,
          data,
          userId
        );
        toast.success('Record updated successfully');
      }
      
      setShowRecordModal(false);
      setEditingRecord(null);
      loadRecords();
    } catch (error) {
      console.error('Failed to save record:', error);
      throw error;
    }
  };

  const handleExportCSV = () => {
    const recordsToExport = selectedRecords.length > 0
      ? records.filter(r => selectedRecords.includes(r.id))
      : records;

    if (recordsToExport.length === 0) {
      toast.warning('No records to export');
      return;
    }

    const csv = dataManagementService.exportToCSV(recordsToExport, selectedSchema.fields);
    const filename = `${selectedSchema.tableName}_${new Date().toISOString().split('T')[0]}.csv`;
    dataManagementService.downloadCSV(csv, filename);
    
    toast.success(`Exported ${recordsToExport.length} record(s)`);
  };

  const handleDeleteTable = async () => {
    const confirmed = window.confirm(
      `⚠️ WARNING: This will permanently delete the "${selectedSchema.tableName}" table and ALL its data (${recordCount} records). This action CANNOT be undone. Are you absolutely sure?`
    );

    if (!confirmed) return;

    // Double confirmation
    const doubleConfirm = window.confirm(
      `This is your last chance. Type the table name to confirm deletion.\n\nClick OK if you're sure, or Cancel to abort.`
    );

    if (!doubleConfirm) return;

    try {
      await dataManagementService.deleteTableSchema(
        appPrefix,
        selectedSchema.tableName,
        selectedSchema.id,
        userId
      );
      toast.success('Table deleted successfully');
      setSelectedSchema(null);
      setRecords([]);
      setSelectedRecords([]);
      loadSchemas(true);
    } catch (error) {
      console.error('Failed to delete table:', error);
      toast.error('Failed to delete table');
    }
  };

  if (loadingSchemas) {
    return (
      <div className="manage-data-tab">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading tables...</p>
        </div>
      </div>
    );
  }

  if (schemas.length === 0) {
    return (
      <div className="manage-data-tab">
        <div className="empty-state">
          <Database size={64} />
          <h2>No Tables Found</h2>
          <p>No table schemas found in the database</p>
          <div className="diagnostic-info">
            <h3>🔍 Diagnostic Information:</h3>
            <ul>
              <li><strong>App Prefix:</strong> {appPrefix || 'undefined'}</li>
              <li><strong>User ID:</strong> {userId || 'undefined'}</li>
              <li><strong>API Endpoint:</strong> /api/data-schemas/{appPrefix}</li>
              <li><strong>Expected Collection:</strong> {appPrefix}_data_schemas</li>
            </ul>
            
            {/* Show raw API data for debugging */}
            {rawApiData && (
              <div className="debug-raw-data">
                <h4>🐛 Raw API Response:</h4>
                <pre>{JSON.stringify(rawApiData, null, 2)}</pre>
                <p className="debug-hint">
                  ⚠️ The API returned data above, but it resulted in 0 schemas. 
                  This indicates a response parsing issue. Copy this data and check 
                  the browser console for more details.
                </p>
              </div>
            )}
            
            <p className="diagnostic-hint">
              <AlertCircle size={16} />
              If you have existing data tables but don't see them here, 
              check the browser console (F12) for detailed logs. Look for 
              "[ManageDataTab] ============ RESULT ============" to see what was received.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button 
              onClick={() => loadSchemas(true)} 
              className="btn-refresh-schemas"
              disabled={loadingSchemas}
            >
              <RefreshCw size={18} className={loadingSchemas ? 'spinning' : ''} />
              Try Again
            </button>
            <button 
              onClick={() => window.location.hash = '#create'} 
              className="btn-create-first"
            >
              <Plus size={18} />
              Create Your First Table
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-data-tab">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <div className="tab-header">
        <h2>
          <Database size={24} />
          Manage Data
        </h2>
        <button
          onClick={() => loadSchemas(true)}
          className="btn-refresh-schemas"
          disabled={loadingSchemas}
          title="Refresh table list"
        >
          <RefreshCw size={18} className={loadingSchemas ? 'spinning' : ''} />
          Refresh Tables
        </button>
      </div>

      {/* Table Selector */}
      <div className="table-selector-section">
        <div className="table-selector-group">
          <label>Select Table:</label>
          <select
            value={selectedSchema?.id || ''}
            onChange={(e) => handleSchemaChange(e.target.value)}
            className="table-selector"
          >
            {schemas.map(schema => (
              <option key={schema.id} value={schema.id}>
                {schema.tableName} ({schema.recordCount || 0} records)
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={loadRecords}
          className="btn-refresh"
          disabled={!selectedSchema || loading}
          title="Refresh records"
        >
          <RefreshCw size={16} className={loading ? 'spinning' : ''} />
          Refresh Records
        </button>
      </div>

      {selectedSchema && (
        <>
          {/* Table Info */}
          <div className="table-info-section">
            <div className="table-info">
              <h3>{selectedSchema.tableName}</h3>
              <div className="table-meta">
                <span>{selectedSchema.fields.length} custom fields</span>
                <span>•</span>
                <span>{recordCount} total records</span>
              </div>
            </div>
            <div className="table-actions">
              <button
                onClick={handleAddRecord}
                className="btn-add-record"
              >
                <Plus size={16} />
                Add Record
              </button>
              <button
                onClick={handleExportCSV}
                className="btn-export"
                disabled={records.length === 0}
              >
                <Download size={16} />
                Export {selectedRecords.length > 0 ? 'Selected' : 'All'}
              </button>
              {selectedRecords.length > 0 && (
                <button
                  onClick={handleBulkDelete}
                  className="btn-bulk-delete"
                >
                  <Trash2 size={16} />
                  Delete Selected ({selectedRecords.length})
                </button>
              )}
              <button
                onClick={handleDeleteTable}
                className="btn-delete-table"
                title="Delete entire table"
              >
                <Trash2 size={16} />
                Delete Table
              </button>
            </div>
          </div>

          {/* Field Info */}
          <div className="fields-info-section">
            <h4>Fields:</h4>
            <div className="fields-badges">
              {selectedSchema.fields.map((field, index) => (
                <span key={index} className={`field-badge field-${field.type}`}>
                  {field.name}
                  <span className="field-type">{field.type}</span>
                  {field.required && <span className="required-badge">*</span>}
                </span>
              ))}
            </div>
          </div>

          {/* Data Table */}
          <DataTable
            records={records}
            fields={selectedSchema.fields}
            onEdit={handleEditRecord}
            onDelete={handleDeleteRecord}
            selectedRecords={selectedRecords}
            onSelectionChange={setSelectedRecords}
            loading={loading}
          />
        </>
      )}

      {/* Record Modal */}
      {showRecordModal && (
        <RecordModal
          mode={modalMode}
          record={editingRecord}
          fields={selectedSchema.fields}
          onSave={handleSaveRecord}
          onCancel={() => {
            setShowRecordModal(false);
            setEditingRecord(null);
          }}
        />
      )}
    </div>
  );
};

export default ManageDataTab;


